import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { EmployeeRecord, CustomProperty } from "winslow-core";
import { apiGetEmployeeRecord } from "../../../services/EmployeeRecordService";
import { apiGetCustomPropertiesForWorkspace } from "../../../services/EmployeeRecordService";
import { erGetCPValue, erSetCPValue } from "../app-employee-records/employeeRecordUtils";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { AppCustomProperty } from "../app-customize/AppCustomProperty";
import { Icon } from "../../../ds2/components";
import { 
    Phone, 
    Mail, 
    MapPin, 
    User, 
    Building, 
    Calendar,
    Info
} from "lucide-react";
import "./AppEmployeeProfile.css";
import { MyTooltip } from "../../common";
import toast from "react-hot-toast";
import { toastSuccess } from "../../../config";

type AppEmployeeProfileProps = {
    employeeRecord?: EmployeeRecord;
}

export const AppEmployeeProfile = ({ employeeRecord }: AppEmployeeProfileProps) => {
    const { employeeId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const uiStates = useSelector(selectUIStates);
    const storeUser = useSelector(selectUser);
    
    const [employee, setEmployee] = useState<EmployeeRecord | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [emailCopied, setEmailCopied] = useState<boolean>(false);
    const [customProperties, setCustomProperties] = useState<CustomProperty[]>([]);
    const [activeTab, setActiveTab] = useState<string>("Personal");
    const [hasChanges, setHasChanges] = useState<boolean>(false);

    useEffect(() => {
        dispatch(setUIStates({...uiStates, hrisLocation: "Employee Profile"}));
    }, []);

    useEffect(() => {
        if (employeeRecord) {
            setEmployee(employeeRecord);
            setLoading(false);
        } else if (employeeId && employeeId !== "new") {
            (async () => {
                try {
                    const er = await apiGetEmployeeRecord(employeeId);
                    if (er) {
                        setEmployee(er);
                    }
                } catch (error) {
                    console.error("Failed to load employee record:", error);
                } finally {
                    setLoading(false);
                }
            })();
        } else {
            setLoading(false);
        }
    }, [employeeRecord, employeeId]);

    // Fetch custom properties for the workspace
    useEffect(() => {
        if (storeUser?.currentWorkspaceId) {
            (async () => {
                try {
                    const props = await apiGetCustomPropertiesForWorkspace(storeUser.currentWorkspaceId!);
                    if (props) {
                        setCustomProperties(props);
                    }
                } catch (error) {
                    console.error("Failed to load custom properties:", error);
                }
            })();
        }
    }, [storeUser?.currentWorkspaceId]);

    const handleCopyEmail = async () => {
        if (employee?.workEmail) {
            try {
                await navigator.clipboard.writeText(employee.workEmail);
                toast("Copied.", toastSuccess);
            } catch (error) {
                console.error("Failed to copy email:", error);
            }
        }
    };

    const getEmployeeTypeDisplay = (type: string) => {
        const typeMap: { [key: string]: string } = {
            "US_FULL_TIME": "Full Time (US)",
            "US_PART_TIME": "Part Time (US)",
            "INT_FULL_TIME": "Full Time (International)",
            "INT_PART_TIME": "Part Time (International)",
            "US_CONTRACTOR": "Contractor (US)",
            "INT_CONTRACTOR": "Contractor (International)",
            "US_VENDOR": "Vendor (US)"
        };
        return typeMap[type] || type;
    };

    const getHireDate = () => {
        const hireDate = erGetCPValue(employee, "system-hireDate");
        if (hireDate) {
            return new Date(hireDate).toLocaleDateString();
        }
        return "Not specified";
    };

    const getEmployeeId = () => {
        return erGetCPValue(employee, "system-employeeId") || "Not assigned";
    };

    // Get filtered custom properties based on employee type
    const getFilteredCustomProperties = () => {
        if (!employee) return [];
        
        return customProperties.filter(prop => 
            prop.visibleEmployeeTypes.includes(employee.employeeType)
        );
    };

    // Get unique group values from filtered custom properties
    const getUniqueGroups = () => {
        const filteredProps = getFilteredCustomProperties();
        const groups = filteredProps
            .map(prop => prop.group)
            .filter((group): group is string => Boolean(group))
            .filter((group, index, array) => array.indexOf(group) === index);
        
        return groups.sort();
    };

    // Get custom properties for a specific group
    const getCustomPropertiesForGroup = (group: string) => {
        return getFilteredCustomProperties().filter(prop => prop.group === group);
    };

    // Get custom properties without a group (for "Personal" tab)
    const getUngroupedCustomProperties = () => {
        return getFilteredCustomProperties().filter(prop => !prop.group);
    };

    // Handle custom property changes
    const handleCustomPropertyChange = (label: string, data: any) => {
        if (!employee) return;
        
        const updatedEmployee = {
            ...employee,
            customProperties: erSetCPValue(employee, label, data)
        };
        
        setEmployee(updatedEmployee);
        setHasChanges(true);
    };

    if (loading) {
        return (
            <div className="app-employee-profile-loading">
                <div className="loading-spinner">Loading employee profile...</div>
            </div>
        );
    }

    if (!employee) {
        return (
            <div className="app-employee-profile-error">
                <div className="error-message">Employee not found</div>
            </div>
        );
    }

    const firstName = erGetCPValue(employee, "system-firstName") || "Unknown";
    const lastName = erGetCPValue(employee, "system-lastName") || "Unknown";
    const fullName = `${firstName} ${lastName}`;
    const title = erGetCPValue(employee, "system-title") || "No title";
    const workPhone = erGetCPValue(employee, "system-workPhone") || "Not provided";
    const location = erGetCPValue(employee, "system-location") || "Not specified";
    const managerName = erGetCPValue(employee, "system-managerName") || "Not assigned";
    const department = erGetCPValue(employee, "system-department") || "Not specified";

    return (
        <div className="app-employee-profile">
            {/* Header Banner */}
            <div className="app-employee-profile-header">
                <div className="app-employee-profile-header-content">
                    <div className="app-employee-profile-avatar">
                        {employee.avatarURL ? (
                            <img 
                                src={employee.avatarURL} 
                                alt={fullName}
                                className="app-employee-profile-avatar-img"
                            />
                        ) : (
                            <div className="app-employee-profile-avatar-placeholder">
                                {firstName.charAt(0)}{lastName.charAt(0)}
                            </div>
                        )}
                    </div>
                    <div className="app-employee-profile-header-info">
                        <h1 className="app-employee-profile-name">{fullName}</h1>
                        <p className="app-employee-profile-title">{title}</p>
                    </div>
                </div>
            </div>

            <div className="app-employee-profile-content">
                {/* Left Rail */}
                <div className="app-employee-profile-left-rail">
                    <div className="app-employee-profile-section">
                        <h3 className="app-employee-profile-section-title">Contact Information</h3>
                        
                        <div className="app-employee-profile-info-item">
                            <Icon
                                variant="secondary"
                                size="sm"
                                icon={<Phone size={16} />}
                            />
                            <span className="app-employee-profile-info-label">Work Phone</span>
                            <span className="app-employee-profile-info-value">{workPhone}</span>
                        </div>

                        <div className="app-employee-profile-info-item app-employee-profile-email-item">
                            <Icon
                                variant="secondary"
                                size="sm"
                                icon={<Mail size={16} />}
                            />
                            <span className="app-employee-profile-info-label">Email Address</span>
                            <span 
                                className="app-employee-profile-info-value app-employee-profile-email"
                                onClick={handleCopyEmail}
                            >
                                {employee.workEmail}
                            </span>
                        </div>
                    </div>

                    <div className="app-employee-profile-section">
                        <h3 className="app-employee-profile-section-title">Manager</h3>
                        <div className="app-employee-profile-info-item">
                            <Icon
                                variant="secondary"
                                size="sm"
                                icon={<User size={16} />}
                            />
                            <span className="app-employee-profile-info-label">Manager</span>
                            <span className="app-employee-profile-info-value">{managerName}</span>
                        </div>
                    </div>

                    <div className="app-employee-profile-section">
                        <h3 className="app-employee-profile-section-title">Employment Details</h3>
                        
                        <div className="app-employee-profile-info-item">
                            <Icon
                                variant="secondary"
                                size="sm"
                                icon={<Calendar size={16} />}
                            />
                            <span className="app-employee-profile-info-label">Hire Date</span>
                            <span className="app-employee-profile-info-value">{getHireDate()}</span>
                        </div>


                        <div className="app-employee-profile-info-item">
                            <Icon
                                variant="secondary"
                                size="sm"
                                icon={<Building size={16} />}
                            />
                            <span className="app-employee-profile-info-label">Employee Type</span>
                            <span className="app-employee-profile-info-value">{getEmployeeTypeDisplay(employee.employeeType)}</span>
                        </div>

                        <div className="app-employee-profile-info-item">
                            <Icon
                                variant="secondary"
                                size="sm"
                                icon={<Building size={16} />}
                            />
                            <span className="app-employee-profile-info-label">Department</span>
                            <span className="app-employee-profile-info-value">{department}</span>
                        </div>

                        <div className="app-employee-profile-info-item">
                            <Icon
                                variant="secondary"
                                size="sm"
                                icon={<MapPin size={16} />}
                            />
                            <span className="app-employee-profile-info-label">Location</span>
                            <span className="app-employee-profile-info-value">{location}</span>
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="app-employee-profile-main-content">
                    <div className="app-employee-profile-tabs">
                        {/* Personal tab (always first) */}
                        <div 
                            className={`app-employee-profile-tab ${activeTab === "Personal" ? "app-employee-profile-tab-active" : ""}`}
                            onClick={() => setActiveTab("Personal")}
                        >
                            Personal
                        </div>
                        
                        {/* Dynamic tabs based on custom property groups */}
                        {getUniqueGroups().map((group) => (
                            <div 
                                key={group}
                                className={`app-employee-profile-tab ${activeTab === group ? "app-employee-profile-tab-active" : ""}`}
                                onClick={() => setActiveTab(group)}
                            >
                                {group}
                            </div>
                        ))}
                    </div>

                    <div className="app-employee-profile-tab-content">
                        {activeTab === "Personal" && (
                            <>
                                {/* <h2 className="app-employee-profile-content-title">Personal Information</h2>
                                <p className="app-employee-profile-content-description">
                                    View and manage personal information for {fullName}.
                                </p> */}
                                
                                {getUngroupedCustomProperties().length > 0 ? (
                                    <div className="app-employee-profile-custom-properties">
                                        {getUngroupedCustomProperties().map((prop) => (
                                            <div key={prop.id} className="app-employee-profile-custom-property-wrapper">
                                                <div className="app-employee-profile-custom-property-label">
                                                    {prop.display}
                                                    &nbsp;
                                                    <MyTooltip
                                                        title={prop.description}
                                                    >  
                                                        <Info size="12" color="var(--text-color-secondary)"/>
                                                     </MyTooltip>
                                                </div>
                                                <div className="app-employee-profile-custom-property-component">
                                                    <AppCustomProperty
                                                        customProperty={prop}
                                                        data={erGetCPValue(employee, prop.label)}
                                                        disabled={true}
                                                        validate={false}
                                                        onChange={handleCustomPropertyChange}
                                                    />
                                                </div>
                                                
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <></>
                                    // <div className="app-employee-profile-content-placeholder">
                                    //     <div className="app-employee-profile-placeholder-icon">
                                    //         <User size={48} />
                                    //     </div>
                                    //     <h3>Personal Information</h3>
                                    //     <p>Personal details and contact information will be displayed here.</p>
                                    // </div>
                                )}
                            </>
                        )}
                        
                        {activeTab !== "Personal" && getCustomPropertiesForGroup(activeTab).length > 0 && (
                            <>
                                {/* <h2 className="app-employee-profile-content-title">{activeTab}</h2>
                                <p className="app-employee-profile-content-description">
                                    {activeTab} information for {fullName}.
                                </p> */}
                                
                                <div className="app-employee-profile-custom-properties">
                                    {getCustomPropertiesForGroup(activeTab).map((prop) => (
                                        <div key={prop.id} className="app-employee-profile-custom-property-wrapper">
                                            <div className="app-employee-profile-custom-property-label">
                                                {prop.display}
                                                &nbsp;
                                                <MyTooltip
                                                    title={prop.description}
                                                >  
                                                    <Info size="12" color="var(--text-color-secondary)"/>
                                                    </MyTooltip>
                                            </div>
                                            <div className="app-employee-profile-custom-property-component">
                                                <AppCustomProperty
                                                    customProperty={prop}
                                                    data={erGetCPValue(employee, prop.label)}
                                                    disabled={true}
                                                    validate={false}
                                                    onChange={handleCustomPropertyChange}
                                                />
                                            </div>
                                            {/* {prop.description && (
                                                <div className="app-employee-profile-custom-property-description">
                                                    {prop.description}
                                                </div>
                                            )} */}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        
                        {activeTab !== "Personal" && getCustomPropertiesForGroup(activeTab).length === 0 && (
                            <></>
                            // <div className="app-employee-profile-content-placeholder">
                            //     <div className="app-employee-profile-placeholder-icon">
                            //         <User size={48} />
                            //     </div>
                            //     <h3>No {activeTab} Information</h3>
                            //     <p>No {activeTab.toLowerCase()} information is available for this employee.</p>
                            // </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
